import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { PORTAL_ASSET_IMAGE_TYPES, PORTAL_ASSET_VIDEO_TYPES } from '../../lib/portalAssets/allowlist';
import { currentConsent, currentKyckConsent } from '../../lib/portalAssets/consent';
import { formatBytes } from '../../lib/portalAssets/gridView';
import type { PortalAssetsApi } from './assetsApi';
import { uploadAssetFiles, uploadSummary, type UploadRow } from './uploadFiles';

// THE UPLOAD SCREEN (spec §4.5), mobile-first. Shared by both hosts.
//
// ⚠️⚠️ CONSENT COMES FIRST AND IS NOT PRE-TICKED. The file button stays disabled
// until it is ticked, and the version sent with the upload is the version whose
// words were ON SCREEN (currentConsent()) — the server stores that version and
// refuses any other, so a page open across a wording change is told to reload
// rather than recording words the uploader never saw.
//
// ⚠️ "Keep this page open" is said, and the browser is asked to warn on leaving
// while anything is uploading: closing the tab kills a resumable session's
// browser half, and a half-uploaded file is cleaned up later, not resumed.
//
// ⚠️ Every hook runs before any conditional render (#310).

const ACCEPT = [
  ...Object.keys(PORTAL_ASSET_IMAGE_TYPES),
  ...Object.keys(PORTAL_ASSET_VIDEO_TYPES),
  '.heic',
  '.heif',
].join(',');

const ROW_LABEL: Record<UploadRow['status'], string> = {
  refused: 'Not accepted',
  waiting: 'Waiting',
  uploading: 'Uploading',
  done: 'Uploaded',
  failed: 'Failed',
};

export function AssetUploader({
  api,
  audience,
  accountId,
  noAccountReason,
  onFinished,
}: {
  api: Pick<PortalAssetsApi, 'createUploads' | 'complete'>;
  /** Which words are shown: a club asserts permission; Kyck acknowledges the club holds it. */
  audience: 'club' | 'staff';
  /** null = no account chosen yet; the file button explains why it is off. */
  accountId: string | null;
  noAccountReason?: string;
  onFinished?: () => void;
}) {
  const consent = audience === 'staff' ? currentKyckConsent() : currentConsent();
  const consentId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [agreed, setAgreed] = useState(false);
  // ⚠️ Offered to staff (2026-10-11) AND clubs (2026-10-12). DEFAULT OFF — ticking is a deliberate act, and defaulting
  // it on would change the review flow for every club without anyone choosing that. Resets per page load: an
  // approval is a decision about THESE files, not a preference to remember. ⚠️ A SEPARATE tick from consent, always.
  const [approve, setApprove] = useState(false);
  const approveId = useId();
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [running, setRunning] = useState(false);
  // ONE sentence when the server's duplicate check could not run (never silent: a guard that cannot run must not look like a pass).
  const [notice, setNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const canChoose = agreed && accountId !== null && !running;

  async function start(files: File[]) {
    if (!canChoose || files.length === 0 || accountId === null) return;
    setRunning(true);
    setNotice(null);
    try {
      await uploadAssetFiles({ api, accountId, consentWordingVersion: consent.version,
        approveOnUpload: approve,
        files,
        onChange: setRows,
        onNotice: setNotice,
      });
    } finally {
      setRunning(false);
      onFinished?.();
    }
  }

  const summary = uploadSummary(rows);

  return (
    <div className="max-w-xl space-y-5">
      <label htmlFor={consentId} className="flex items-start gap-3 rounded-md border border-border bg-card p-3 text-sm text-foreground">
        <input
          id={consentId}
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0"
          checked={agreed}
          disabled={running}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <span>{consent.text}</span>
      </label>

      <label htmlFor={approveId} className="flex items-start gap-3 rounded-md border border-border bg-card p-3 text-sm text-foreground">
        <input
          id={approveId}
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0"
          checked={approve}
          disabled={running}
          onChange={(e) => setApprove(e.target.checked)}
        />
        <span>
          Approve these files as they upload.
          <span className="block text-xs text-muted-foreground">
            They become usable once processing finishes. Recorded as approved at upload by you.
          </span>
        </span>
      </label>

      <div
        onDragOver={(e) => {
          if (!canChoose) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void start(Array.from(e.dataTransfer.files));
        }}
        className={`rounded-md border-2 border-dashed p-4 ${dragging ? 'border-ring' : 'border-border'}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="sr-only"
          tabIndex={-1}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = '';
            void start(files);
          }}
        />
        <Button type="button" className="w-full" disabled={!canChoose} onClick={() => inputRef.current?.click()}>
          Choose photos and videos
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          {accountId === null
            ? noAccountReason ?? 'Choose a location first.'
            : !agreed
              ? 'Tick the box above first.'
              : 'JPEG, PNG, WebP or HEIC photos up to 50 MB; MP4 or MOV videos up to 2 GB. You can also drop files here.'}
        </p>
      </div>

      {summary && (
        <p className="text-sm text-foreground" role="status">
          {summary}
        </p>
      )}
      {notice && (
        <p className="text-sm text-amber-700" role="status">
          {notice}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {rows.map((r) => (
            <li key={r.key} className="space-y-1 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-foreground" title={r.filename}>{r.filename}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {ROW_LABEL[r.status]}
                  {r.status === 'uploading' ? ` ${Math.round(r.progress * 100)}%` : ''} · {formatBytes(r.sizeBytes)}
                </span>
              </div>
              {r.status === 'uploading' && (
                <div className="h-1.5 overflow-hidden rounded bg-muted" aria-hidden="true">
                  <div className="h-full bg-primary" style={{ width: `${Math.round(r.progress * 100)}%` }} />
                </div>
              )}
              {r.reason && <p className="text-xs text-foreground">{r.reason}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
